import stringify from 'json-stable-stringify';
import {useEffect} from 'react';

import {
  RETRIEVE_PRIMARY_SAMPLES_INTERVAL_MS,
  SampleLookupInfo,
  StructuredTable,
  StructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import useOnMount from 'ui-library/hooks/useOnMount';

import {fetchStructuredTableLiveData} from './fetchStructuredTableLiveData';

/**
 * This hook ensures that live data is fetched once on mount.
 */
function useLiveDataOnMount<TTableMetadata, TRowMetadata>(
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  sampleLookupInfos: Array<SampleLookupInfo>,
  onFetchComplete: () => void,
  structuredTable?: StructuredTable<TTableMetadata, TRowMetadata>
): void {
  useOnMount(() => {
    fetchStructuredTableLiveData(adapter, sampleLookupInfos, structuredTable).then(() => {
      onFetchComplete();
    });
  });
}

/**
 * This hook ensures that live data is fetched on mount, and then at regular intervals.
 */
function useRecurringLiveData<TTableMetadata, TRowMetadata>(
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  sampleLookupInfos: Array<SampleLookupInfo>,
  onFetchComplete: () => void,
  structuredTable?: StructuredTable<TTableMetadata, TRowMetadata>
): void {
  useEffect(() => {
    // Fetch periodically
    const intervalId = setInterval(() => {
      fetchStructuredTableLiveData(adapter, sampleLookupInfos, structuredTable).then(() => {
        onFetchComplete();
      });
    }, RETRIEVE_PRIMARY_SAMPLES_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };

    // The list of sampleLookupInfos must be compared in a stable way. This is an array of objects,
    // so JSON.stringify is safe and stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, stringify(sampleLookupInfos), onFetchComplete]);
}

/**
 * This hook ensures that live data is fetched on mount, and then some at regular intervals.
 *
 * @param onFetchComplete - a callback that will be invoked on fetch completion. This is useful for
 *  triggering re-renders once the fetch is complete.
 */
export default function useLiveData<TTableMetadata, TRowMetadata>(
  adapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>,
  sampleLookupInfos: Array<SampleLookupInfo>,
  onFetchComplete: () => void,
  structuredTable?: StructuredTable<TTableMetadata, TRowMetadata>
): void {
  // Fetch all data that should be fetched on mount.
  useLiveDataOnMount(adapter, sampleLookupInfos, onFetchComplete, structuredTable);

  // Fetch all data that should be fetched on a regular interval afterwards.
  useRecurringLiveData(adapter, sampleLookupInfos, onFetchComplete, structuredTable);
}
